import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { AUTH_MODES, ERROR_MESSAGES } from '../../../constants/authConstants';

const AuthForm = ({ mode, onSubmit, onSwitchMode, isLoading }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
    voiceDescription: '',
    agreeToTerms: false
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target || {};
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = ERROR_MESSAGES?.REQUIRED_EMAIL;
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = ERROR_MESSAGES?.INVALID_EMAIL;
    }

    if (!formData?.password) {
      newErrors.password = ERROR_MESSAGES?.REQUIRED_PASSWORD;
    } else if (formData?.password?.length < 6) {
      newErrors.password = ERROR_MESSAGES?.PASSWORD_TOO_SHORT;
    }

    if (mode === AUTH_MODES?.REGISTER) {
      if (!formData?.username) {
        newErrors.username = ERROR_MESSAGES?.REQUIRED_USERNAME;
      } else if (formData?.username?.length < 3) {
        newErrors.username = ERROR_MESSAGES?.USERNAME_TOO_SHORT;
      }

      if (!formData?.voiceDescription) {
        newErrors.voiceDescription = ERROR_MESSAGES?.REQUIRED_VOICE_DESC;
      }

      if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = ERROR_MESSAGES?.PASSWORD_MISMATCH;
      }

      if (!formData?.agreeToTerms) {
        newErrors.agreeToTerms = ERROR_MESSAGES?.REQUIRED_TERMS;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="auth-form w-full" noValidate>
      {mode === AUTH_MODES?.REGISTER && (
        <>
          <Input
            label="Имя пользователя"
            type="text"
            name="username"
            placeholder="Ваше имя в эфире"
            value={formData?.username}
            onChange={handleInputChange}
            error={errors?.username}
            required
            className="mb-4"
            autoComplete="username"
            aria-label="Имя пользователя"
            aria-required="true"
            aria-invalid={!!errors?.username}
          />

          <div className="mb-4">
            <label 
              htmlFor="voiceDescription" 
              className="block text-sm font-medium text-foreground mb-2"
            >
              Описание голоса <span className="text-error" aria-label="обязательное поле">*</span>
            </label>
            <textarea
              id="voiceDescription"
              name="voiceDescription"
              placeholder="Расскажите о своем голосе и стиле вещания..."
              value={formData?.voiceDescription}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 resize-none"
              aria-label="Описание голоса"
              aria-required="true"
              aria-invalid={!!errors?.voiceDescription}
              aria-describedby={errors?.voiceDescription ? 'voiceDescription-error' : undefined}
            />
            {errors?.voiceDescription && (
              <p id="voiceDescription-error" className="mt-1 text-sm text-error" role="alert">
                {errors?.voiceDescription}
              </p>
            )}
          </div>
        </>
      )}
      <Input
        label="Email"
        type="email"
        name="email"
        placeholder="example@email.com"
        value={formData?.email}
        onChange={handleInputChange}
        error={errors?.email}
        required
        className="mb-4"
        autoComplete="email"
        aria-label="Email адрес"
        aria-required="true"
        aria-invalid={!!errors?.email}
      />
      <Input
        label="Пароль"
        type="password"
        name="password"
        placeholder="Минимум 6 символов"
        value={formData?.password}
        onChange={handleInputChange}
        error={errors?.password}
        required
        className="mb-4"
        autoComplete={mode === AUTH_MODES?.LOGIN ? 'current-password' : 'new-password'}
        aria-label="Пароль"
        aria-required="true"
        aria-invalid={!!errors?.password}
      />
      {mode === AUTH_MODES?.REGISTER && (
        <>
          <Input
            label="Подтвердите пароль"
            type="password"
            name="confirmPassword"
            placeholder="Повторите пароль"
            value={formData?.confirmPassword}
            onChange={handleInputChange}
            error={errors?.confirmPassword}
            required
            className="mb-4"
            autoComplete="new-password"
            aria-label="Подтверждение пароля"
            aria-required="true"
            aria-invalid={!!errors?.confirmPassword}
          />

          <div className="mb-6">
            <Checkbox
              label="Я согласен с условиями использования и политикой конфиденциальности"
              checked={formData?.agreeToTerms}
              onChange={handleInputChange}
              name="agreeToTerms"
              error={errors?.agreeToTerms}
              aria-label="Согласие с условиями"
              aria-required="true"
            />
          </div>
        </>
      )}
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        className="mb-4"
        disabled={isLoading}
        aria-label={mode === AUTH_MODES?.LOGIN ? 'Войти в эфир' : 'Создать канал'}
      >
        {mode === AUTH_MODES?.LOGIN ? 'Войти в эфир' : 'Создать канал'}
      </Button>
      <div className="text-center">
        <button
          type="button"
          onClick={onSwitchMode}
          disabled={isLoading}
          className="text-sm text-primary hover:text-primary/80 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={mode === AUTH_MODES?.LOGIN ? 'Перейти к регистрации' : 'Перейти ко входу'}
        >
          {mode === AUTH_MODES?.LOGIN ? 'Нет аккаунта? Создайте свой канал' : 'Уже есть аккаунт? Войдите'}
        </button>
      </div>
    </form>
  );
};

export default AuthForm;